import { FC } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { TaskWithAssigneesAndProject } from 'types/store/tasksList';
import { CentralizedTasksTableTab } from 'utils/types/centralizedTasks';
import RenderNoRecords from 'Atoms/NoRecords/NoRecords';
import TableEmptyErrorState from 'Molecules/TableEmptyErrorState/TableEmptyErrorState';
import TaskRow from '../TaskRow/TaskRow';
import CreateTaskModal from '../../CreateTaskModal/CreateTaskModal';
import LastTaskRow from '../TaskRow/LastTaskRow/LastTaskRow';
import useTaskRowActions from 'Pages/ProjectTasksList/TasksTable/TableBody/useTaskRowActions';
import emptyTasksTable from 'assets/icons/empty-tasks.svg';
import { selectIsUserFacilitator } from 'state/User/userSlice';

interface TableBodyProps {
  tasks: TaskWithAssigneesAndProject[];
  canFetchMoreTasks: boolean;
  fetchMoreTasks: () => void;
  tableName: CentralizedTasksTableTab;
  refreshTasks: () => void;
  hasError?: boolean;
}

const TableBody: FC<TableBodyProps> = ({
  tasks,
  canFetchMoreTasks,
  fetchMoreTasks,
  tableName,
  refreshTasks,
  hasError = false,
}) => {
  const isListEmpty = tasks.length === 0;
  const isUserFacilitator = useSelector(selectIsUserFacilitator);

  const { openTakInSidePanel } = useTaskRowActions({
    isCentralizedTasks: true,
  });

  return (
    <tbody>
      {tasks.map((task: TaskWithAssigneesAndProject, index: number) => {
        const isOdd = index % 2 !== 0;
        return (
          <TaskRow
            key={task.id}
            task={task}
            bgColor={isOdd ? 'bg-neutral-lightest-two' : 'bg-neutral-white'}
            openTakInSidePanel={openTakInSidePanel}
            refreshTasks={refreshTasks}
            tableName={tableName}
          />
        );
      })}
      {!isListEmpty && canFetchMoreTasks && (
        <LastTaskRow
          isOdd={tasks.length % 2 !== 0}
          tableName={tableName}
          onLastRowInView={fetchMoreTasks}
        />
      )}
      {isListEmpty && (
        <tr>
          <td colSpan={10}>
            {hasError ? (
              <TableEmptyErrorState />
            ) : (
              <RenderNoRecords
                dataCy='tasks-list-empty-table'
                imageSrc={emptyTasksTable}
                imageClassName='-ml-4'
                className='h-empty-table-body w-full'
                caption={intl.get('TASKS_LIST_PAGE.TABLE.EMPTY')}
                labelClassName='mt-0'
              >
                <div className='flex-initial self-center mt-2'>
                  <CreateTaskModal isUserFacilitator={isUserFacilitator} />
                </div>
              </RenderNoRecords>
            )}
          </td>
        </tr>
      )}
    </tbody>
  );
};

export default TableBody;
